/**
 * ContactDetailPanel
 * ------------------
 * Self-contained contact detail panel that replicates the full
 * contact detail modal from Contacts.tsx, designed to be used
 * inside a DemandSlidePanel (slide-in overlay) within CityModal.
 *
 * Props:
 *   contatoId  – number | null  (fetches contact data internally)
 *   onClose    – () => void
 */

import React, {
  useState, useEffect, useRef, useLayoutEffect,
} from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import TagSelector, { Tag } from './TagSelector';
import { API_BASE_URL } from '../../lib/apiConfig';
import { apiFetch } from '../../lib/apiFetch';
import citiesData from '../../data/cities_with_coordinates.json';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CityData {
  id: number;
  nome: string;
  microrregiao: { mesorregiao: { UF: { sigla: string } } };
}

interface Eleitor {
  id: number;
  nome: string;
  telefone: string | null;
  email: string | null;
  cidade_id: number | null;
  data_nascimento: string | null;
  profissao: string | null;
  time_coracao: string | null;
  rede_social: string | null;
  observacoes: string | null;
  multiplicador?: boolean;
  cidade?: { id: number; nome: string; uf: string } | null;
  assessor?: { id: number; nome: string } | null;
  tags?: Tag[];
}

interface Registro {
  id: number;
  descricao: string;
  created_at: string;
  updated_at: string;
  usuario: { id: number; nome: string };
}

interface LogEntry {
  id: number;
  data_hora: string;
  acao: string;
  observacao: string;
  registrou: { id: number; nome: string } | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatPhone = (phone: string | null): string => {
  if (!phone) return '';
  const d = phone.replace(/\D/g, '');
  if (d.length === 13) return `(${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12) return `(${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return phone;
};

const timesRS = [
  'Grêmio', 'Internacional', 'Juventude', 'Caxias', 'Brasil de Pelotas',
  'Ypiranga', 'São José (POA)', 'Esportivo', 'Aimoré', 'São Luiz',
  'Guarany de Bagé', 'Outros',
];

// ─── Styled keyframes ─────────────────────────────────────────────────────────

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const shimmerAnim = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
`;

// ─── Inline-edit sub-components ───────────────────────────────────────────────

const SpinnerCircle = styled.span`
  display: inline-block;
  width: 14px; height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

const HoverShell = styled.div<{ $canEdit: boolean; $block?: boolean }>`
  display: ${p => p.$block ? 'block' : 'inline-flex'};
  align-items: center;
  gap: 6px;
  padding: 4px 8px; margin: -4px -8px;
  border-radius: 6px;
  cursor: ${p => p.$canEdit ? 'pointer' : 'default'};
  transition: background-color 0.15s;
  min-height: 24px;
  ${p => p.$block ? 'flex: 1; min-width: 0;' : ''}
  &:hover { background: ${p => p.$canEdit ? 'rgba(15,23,42,0.05)' : 'transparent'}; }
`;

const IEInput = styled.input`
  width: 100%; padding: 3px 7px;
  border: 1.5px solid var(--accent); border-radius: 4px;
  font-size: 13px; color: #111827; background: #fff;
  box-sizing: border-box;
  &:focus { outline: none; }
  &::placeholder { color: #9ca3af; }
`;

const IETextArea = styled.textarea`
  width: 100%; padding: 10px 12px;
  border: 1.5px solid var(--accent); border-radius: 6px;
  font-size: 13px; color: #111827; font-family: inherit;
  resize: vertical; box-sizing: border-box;
  &:focus { outline: none; }
  &::placeholder { color: #9ca3af; }
`;

const ContactDescription = styled.div`
  font-size: 13px; color: #374151; line-height: 1.6; white-space: pre-wrap;
`;

const IECustomSelectWrapper = styled.div`position: relative; min-width: 0; width: 100%;`;
const IECustomSelectButton = styled.button`
  width: 100%; display: flex; align-items: center; justify-content: space-between;
  gap: 6px; padding: 3px 7px; background: white;
  border: 1.5px solid var(--accent); border-radius: 4px;
  font-size: 13px; color: #111827; cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  svg { width: 12px; height: 12px; flex-shrink: 0; color: #6b7280; }
  &:focus { outline: none; }
`;
const IECustomSelectDropdown = styled.div`
  position: absolute; top: calc(100% + 3px); left: 0;
  min-width: 180px; width: max-content;
  background: white; border: 1px solid #e5e7eb;
  border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 9999; overflow: hidden;
`;
const IEDropdownSearchInput = styled.input`
  width: 100%; padding: 8px 12px; border: none;
  border-bottom: 1px solid #e5e7eb; font-size: 13px; color: #1e293b;
  box-sizing: border-box; outline: none;
  &::placeholder { color: #94a3b8; }
  &:focus { border-bottom-color: var(--accent); }
`;
const IECustomSelectOptionsContainer = styled.div`max-height: 220px; overflow-y: auto;`;
const IECustomSelectOption = styled.div`
  padding: 9px 14px; font-size: 13px; font-weight: 500; color: #1e293b;
  cursor: pointer; transition: background 0.1s;
  &:hover { background: #f8fafc; }
`;

interface IETextProps {
  value: string; canEdit: boolean; saving: boolean;
  onSave: (v: string) => void | Promise<void>;
  placeholder?: string; emptyLabel?: string;
  renderDisplay?: (v: string) => React.ReactNode;
  allowEmpty?: boolean;
}
const InlineEditText: React.FC<IETextProps> = ({
  value, canEdit, saving, onSave, placeholder, emptyLabel = '-',
  renderDisplay, allowEmpty = true,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  useEffect(() => { if (editing) { setDraft(value); committedRef.current = false; } }, [editing, value]);
  useEffect(() => { if (!saving) setPending(false); }, [saving]);
  useLayoutEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
    }
  }, [editing]);

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    setEditing(false);
    const next = draft.trim();
    if (!allowEmpty && !next) return;
    if (next !== (value ?? '')) { setPending(true); onSave(next); }
  };

  if (editing) {
    return (
      <IEInput ref={inputRef} value={draft} placeholder={placeholder}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
      />
    );
  }
  const displayValue = value || emptyLabel;
  return (
    <HoverShell $canEdit={canEdit} onClick={() => canEdit && setEditing(true)}>
      {(saving || pending) ? <SpinnerCircle /> : (renderDisplay ? renderDisplay(displayValue) : displayValue)}
    </HoverShell>
  );
};

interface IETextAreaProps {
  value: string; canEdit: boolean; saving: boolean;
  onSave: (v: string) => void | Promise<void>;
  placeholder?: string; emptyLabel?: string;
}
const InlineEditTextArea: React.FC<IETextAreaProps> = ({
  value, canEdit, saving, onSave, placeholder, emptyLabel = '-',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (!saving) setPending(false); }, [saving]);
  useLayoutEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus();
      taRef.current.setSelectionRange(taRef.current.value.length, taRef.current.value.length);
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== (value ?? '')) { setPending(true); onSave(draft); }
  };

  if (editing) {
    return (
      <IETextArea ref={taRef} value={draft}
        rows={Math.max(4, draft.split('\n').length + 1)}
        placeholder={placeholder}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
      />
    );
  }
  return (
    <HoverShell $canEdit={canEdit} $block onClick={() => canEdit && setEditing(true)}>
      {(saving || pending) ? <SpinnerCircle /> : <ContactDescription>{value || emptyLabel}</ContactDescription>}
    </HoverShell>
  );
};

interface IESelectOption { value: string; label: string; }
interface IESelectProps {
  value: string | undefined; canEdit: boolean; saving: boolean;
  options: IESelectOption[]; searchable?: boolean; searchPlaceholder?: string;
  emptyLabel?: string; onSave: (v: string) => void | Promise<void>;
  renderDisplay?: (label: string) => React.ReactNode;
}
const InlineEditSelect: React.FC<IESelectProps> = ({
  value, canEdit, saving, options, searchable, searchPlaceholder,
  emptyLabel = '-', onSave, renderDisplay,
}) => {
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setEditing(false); setSearch('');
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [editing]);

  const currentLabel = value ? (options.find(o => o.value === value)?.label ?? value) : '';
  if (!editing) {
    const display = currentLabel || emptyLabel;
    return (
      <HoverShell $canEdit={canEdit} onClick={() => canEdit && setEditing(true)}>
        {saving ? <SpinnerCircle /> : (renderDisplay ? renderDisplay(display) : display)}
      </HoverShell>
    );
  }
  const filtered = searchable
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())).slice(0, 50)
    : options;

  return (
    <IECustomSelectWrapper ref={wrapperRef}>
      <IECustomSelectButton onClick={e => e.stopPropagation()}>
        {currentLabel || 'Selecione'}
        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7" /></svg>
      </IECustomSelectButton>
      <IECustomSelectDropdown>
        {searchable && (
          <IEDropdownSearchInput type="text" placeholder={searchPlaceholder ?? 'Buscar...'}
            value={search} autoFocus
            onChange={e => setSearch(e.target.value)}
            onClick={e => e.stopPropagation()}
          />
        )}
        <IECustomSelectOptionsContainer>
          {filtered.map(opt => (
            <IECustomSelectOption key={opt.value}
              onClick={() => { setEditing(false); setSearch(''); if (opt.value !== value) onSave(opt.value); }}>
              {opt.label}
            </IECustomSelectOption>
          ))}
        </IECustomSelectOptionsContainer>
      </IECustomSelectDropdown>
    </IECustomSelectWrapper>
  );
};

interface IEDateProps {
  value: string | undefined; canEdit: boolean; saving: boolean;
  onSave: (v: string | null) => void | Promise<void>;
  emptyLabel?: string; renderDisplay?: (formatted: string) => React.ReactNode;
}
const InlineEditDate: React.FC<IEDateProps> = ({
  value, canEdit, saving, onSave, emptyLabel = '-', renderDisplay,
}) => {
  const isoValue = value ? value.slice(0, 10) : '';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(isoValue);
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  useEffect(() => { if (editing) { setDraft(isoValue); committedRef.current = false; } }, [editing, isoValue]);
  useEffect(() => { if (!saving) setPending(false); }, [saving]);
  useLayoutEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    setEditing(false);
    if (draft !== isoValue) { setPending(true); onSave(draft || null); }
  };

  if (editing) {
    return (
      <IEInput ref={inputRef} type="date" value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setDraft(isoValue); setEditing(false); }
        }}
      />
    );
  }
  const formatted = value ? (() => {
    const parts = value.slice(0, 10).split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])).toLocaleDateString('pt-BR');
    }
    return value;
  })() : emptyLabel;

  return (
    <HoverShell $canEdit={canEdit} onClick={() => canEdit && setEditing(true)}>
      {(saving || pending) ? <SpinnerCircle /> : (renderDisplay ? renderDisplay(formatted) : formatted)}
    </HoverShell>
  );
};

const MultiplicadorBadge = styled.span<{ $active: boolean }>`
  font-size: 12px; font-weight: 600; padding: 2px 8px; border-radius: 999px;
  background: ${p => p.$active ? '#fef3c7' : '#f1f5f9'};
  color: ${p => p.$active ? '#92400e' : '#64748b'};
`;

const InlinePhoneButton = styled.button<{ $color: string }>`
  flex-shrink: 0; width: 24px; height: 24px; border-radius: 6px;
  border: none; background: ${p => p.$color}18; color: ${p => p.$color};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.15s;
  svg { width: 13px; height: 13px; }
  &:hover { background: ${p => p.$color}; color: white; }
`;

// ─── Panel Layout Styled Components ──────────────────────────────────────────

const PanelWrap = styled.div`
  position: absolute;
  inset: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CDHeader = styled.div`
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 18px 24px 14px;
  flex-shrink: 0;
`;

const CDHeaderTop = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 10px;
`;

const CDHeaderBottom = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; flex-wrap: wrap;
`;

const CDMeta = styled.div`display: flex; align-items: center; gap: 6px; flex-wrap: wrap;`;
const CDMetaChip = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 10px; background: #f3f4f6; border-radius: 999px;
  font-size: 11px; font-weight: 600; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.05em;
`;
const CDMetaDot = styled.span`color: #d1d5db; font-size: 13px; line-height: 1;`;
const CDMetaText = styled.span`font-size: 12px; color: #6b7280;`;
const CDHeaderActions = styled.div`display: flex; align-items: center; gap: 6px;`;
const CDNameRow = styled.div`display: flex; align-items: center; gap: 12px; flex-wrap: wrap;`;
const CDName = styled.h2`
  margin: 0; font-size: 22px; font-weight: 700; color: #111827; line-height: 1.2;
  display: flex; align-items: center;
  input { font-size: 22px; font-weight: 700; color: #111827; line-height: 1.2;
    padding: 2px 6px; border-radius: 6px; min-width: 200px; }
`;
const CDMultiplicadorBadge = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 12px; background: #fef3c7; color: #d97706;
  border: 1px solid #fde68a; border-radius: 999px;
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
`;
const CDActionBtns = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap;`;
const CDActionBtn = styled.button<{ $primary?: boolean }>`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 16px; border-radius: 8px;
  font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s;
  white-space: nowrap;
  background: ${p => p.$primary ? '#25d366' : '#f9fafb'};
  color: ${p => p.$primary ? '#fff' : '#374151'};
  border: ${p => p.$primary ? 'none' : '1px solid #e5e7eb'};
  &:hover { background: ${p => p.$primary ? '#22c55e' : '#f3f4f6'}; }
`;
const CDIconBtn = styled.button`
  width: 34px; height: 34px; display: flex; align-items: center; justify-content: center;
  border-radius: 8px; border: 1px solid #e5e7eb; background: #f9fafb; color: #6b7280;
  cursor: pointer; transition: all 0.15s; flex-shrink: 0;
  &:hover { background: #f3f4f6; color: #374151; }
  svg { width: 16px; height: 16px; }
`;
const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
  &:hover { background: #e5e7eb; }
  svg { width: 16px; height: 16px; }
`;

const CDBody = styled.div`display: flex; flex: 1; min-height: 0; overflow: hidden;`;

const CDLeft = styled.div`
  flex: 1; overflow-y: auto; padding: 20px 24px;
  display: flex; flex-direction: column; gap: 24px;
`;
const CDRight = styled.div`
  width: 380px; min-width: 380px; flex-shrink: 0;
  border-left: 1px solid #e5e7eb; display: flex; flex-direction: column;
  background: #fff; min-height: 0;
`;

const CDSection = styled.div`display: flex; flex-direction: column;`;
const CDSectionTitle = styled.div`
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: #9ca3af; margin-bottom: 10px;
`;
const CDInfoGrid = styled.div`display: flex; flex-direction: column; gap: 0;`;
const CDInfoRow = styled.div`
  display: grid; grid-template-columns: 130px 1fr; align-items: center;
  gap: 8px; height: 34px; overflow: visible;
`;
const CDInfoLabel = styled.div`
  font-size: 13px; color: #6b7280; font-weight: 500;
  display: flex; align-items: center; gap: 8px;
  svg { width: 14px; height: 14px; color: #9ca3af; flex-shrink: 0; }
`;
const CDInfoValue = styled.div`
  font-size: 14px; color: #111827; min-width: 0; display: flex; align-items: center;
`;
const CDTodayBadge = styled.span`
  display: inline-flex; align-items: center; padding: 2px 8px;
  background: #dcfce7; color: #16a34a; border-radius: 999px;
  font-size: 10px; font-weight: 700; margin-left: 6px;
`;
const CDInfoColumns = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: start;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

// Right panel
const CDRightTabs = styled.div`
  display: flex; align-items: flex-end; gap: 0;
  border-bottom: 1px solid #f1f5f9; background: #fff; flex-shrink: 0; padding: 0 20px;
`;
const CDRightTab = styled.button<{ $active?: boolean }>`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 14px 4px 12px; margin-right: 20px;
  border: none; background: none;
  font-size: 14px; font-weight: ${p => p.$active ? '600' : '400'};
  cursor: pointer; transition: all 0.15s;
  border-bottom: 2px solid ${p => p.$active ? '#6366f1' : 'transparent'};
  color: ${p => p.$active ? '#6366f1' : '#6b7280'};
  white-space: nowrap;
  &:hover { color: #374151; }
  svg { flex-shrink: 0; }
`;
const CDRightTabCount = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 6px;
  background: #e5e7eb; border-radius: 999px;
  font-size: 11px; font-weight: 600; color: #374151;
`;
const CDRightBody = styled.div`
  flex: 1; overflow-y: auto; padding: 8px 20px 20px; display: flex; flex-direction: column;
`;
const CDRightFooter = styled.div`flex-shrink: 0; padding: 12px 16px 16px; background: #fff;`;
const CDInputBox = styled.div`
  border: 1px solid #e2e8f0; border-radius: 12px; background: white; overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;
const CDInputArea = styled.textarea`
  width: 100%; border: none; padding: 12px 14px 6px;
  font-size: 13px; font-family: inherit; resize: none; outline: none;
  color: #374151; background: transparent; box-sizing: border-box; display: block;
  &::placeholder { color: #9ca3af; }
`;
const CDFooterRow = styled.div`
  display: flex; align-items: center; justify-content: flex-end;
  padding: 6px 10px 8px; gap: 8px;
`;
const CDSendBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 8px; border: none;
  background: #6366f1; color: #fff; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: opacity 0.2s;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: 0.88; }
`;

const CDEmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; padding: 32px 16px; color: #9ca3af; text-align: center;
  svg { width: 32px; height: 32px; opacity: 0.4; }
  span { font-size: 13px; }
`;

// Timeline
const CDFilterBar = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  padding: 16px 20px 12px; flex-shrink: 0;
  background: #fff; border-bottom: 1px solid #f1f5f9;
`;
const CDFilterChips = styled.div`display: flex; gap: 2px;`;
const CDFilterChip = styled.button<{ $active?: boolean }>`
  background: ${p => p.$active ? 'white' : 'transparent'};
  border: ${p => p.$active ? '1px solid #e5e7eb' : '1px solid transparent'};
  border-radius: 7px; padding: 5px 12px;
  font-size: 13px; font-weight: ${p => p.$active ? '600' : '400'};
  color: ${p => p.$active ? '#111827' : '#6b7280'};
  cursor: pointer; transition: all 0.15s;
  box-shadow: ${p => p.$active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none'};
  &:hover { color: #111827; }
`;
const CDDateGroup = styled.div`
  font-size: 11px; font-weight: 700; color: #9ca3af;
  letter-spacing: 0.08em; text-transform: uppercase; padding: 12px 0 6px;
`;
const CDTimeline = styled.div`display: flex; flex-direction: column;`;
const CDTimelineItem = styled.div`display: flex; gap: 12px; align-items: flex-start;`;
const CDTimelineLeft = styled.div`
  display: flex; flex-direction: column; align-items: center; flex-shrink: 0; width: 32px;
`;
const CDTimelineLine = styled.div`
  width: 2px; flex: 1; min-height: 16px; background: #e9edf2; margin: 4px 0;
`;
const CDTimelineContent = styled.div`
  flex: 1; min-width: 0; padding-bottom: 12px; padding-top: 5px;
`;
const CDIconAvatar = styled.div<{ $type: 'comment' | 'history' }>`
  width: 34px; height: 34px; min-height: 34px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.$type === 'comment' ? '#e8eaf6' : '#f0fdf4'};
  color: ${p => p.$type === 'comment' ? '#5c6bc0' : '#16a34a'};
`;
const CDFeedRow = styled.div`
  display: flex; align-items: center; gap: 2px; flex-wrap: wrap;
  line-height: 1.45; font-size: 13px; min-height: 34px;
`;
const CDFeedMeta = styled.div`
  flex: 1; min-width: 0; display: flex; align-items: center;
  flex-wrap: wrap; gap: 0; font-size: 13px;
`;
const CDFeedAuthor = styled.span`font-weight: 700; color: #111827; margin-right: 2px;`;
const CDFeedAction = styled.span`color: #6b7280; font-size: 13px;`;
const CDFeedTime = styled.span`font-size: 12px; color: #9ca3af; flex-shrink: 0; margin-left: 4px;`;
const CDFeedCard = styled.div`
  margin-top: 6px; background: white; border: 1px solid #e9edf2; border-radius: 12px;
  padding: 12px 14px; font-size: 13px; color: #374151; line-height: 1.6;
  white-space: pre-wrap; word-break: break-word; box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;
const CDRegistroMenuWrap = styled.div`position: relative;`;
const CDRegistroMenuBtn = styled.button`
  width: 24px; height: 24px; border-radius: 6px; border: none;
  background: transparent; color: #9ca3af; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: #f3f4f6; color: #374151; }
  svg { width: 14px; height: 14px; }
`;
const RegistroMenu = styled.div`
  position: absolute; top: 100%; right: 0;
  background: white; border: 1px solid #e2e8f0; border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10;
  min-width: 140px; margin-top: 4px; overflow: hidden;
`;
const RegistroMenuItem = styled.button<{ $danger?: boolean }>`
  width: 100%; background: none; border: none; padding: 10px 16px;
  text-align: left; cursor: pointer;
  color: ${p => p.$danger ? '#ef4444' : '#334155'};
  font-size: 14px; font-weight: 500;
  display: flex; align-items: center; gap: 8px; transition: all 0.2s;
  svg { width: 16px; height: 16px; }
  &:hover:not(:disabled) { background: ${p => p.$danger ? '#fef2f2' : '#f8fafc'}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const EditRegistroForm = styled.div`display: flex; flex-direction: column; gap: 12px;`;
const EditTextArea = styled.textarea`
  padding: 12px; background: white; border: 1px solid #e2e8f0;
  border-radius: 6px; color: #1e293b; font-size: 14px; font-family: inherit;
  resize: vertical; transition: all 0.2s;
  &:focus { outline: none; border-color: var(--accent); }
`;
const EditActions = styled.div`display: flex; gap: 8px; justify-content: flex-end;`;
const SaveButton = styled.button`
  background: var(--accent); color: white; border: none; border-radius: 6px;
  padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover { opacity: 0.88; }
`;
const CancelEditButton = styled.button`
  background: #f1f5f9; color: #64748b; border: none; border-radius: 6px;
  padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover { background: #e2e8f0; }
`;

const SpinnerBtn = styled.div`
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.3); border-top-color: white;
  border-radius: 50%; animation: ${spin} 0.6s linear infinite;
`;

// Demandas tab
const DemandaItem = styled.div`
  background: white; border: 1px solid #e2e8f0; border-radius: 8px;
  padding: 16px; transition: all 0.2s; cursor: pointer;
  &:hover { border-color: #cbd5e1; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
`;
const DemandaHeader = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 12px; margin-bottom: 8px;
`;
const DemandaTitle = styled.h4`
  margin: 0; font-size: 15px; font-weight: 600; color: #1e293b; flex: 1;
`;
const DemandaStatus = styled.span<{ $status: string }>`
  padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; white-space: nowrap;
  ${p => {
    switch (p.$status) {
      case 'NAO_INICIADA': return 'background:#dbeafe;color:#1e40af;';
      case 'EM_ANDAMENTO': return 'background:#fef3c7;color:#92400e;';
      case 'PAUSADA':      return 'background:#fef3c7;color:#92400e;';
      case 'CONCLUIDA':    return 'background:#d1fae5;color:#065f46;';
      case 'CANCELADA':    return 'background:#fee2e2;color:#991b1b;';
      default:             return 'background:#f1f5f9;color:#64748b;';
    }
  }}
`;
const DemandaDescription = styled.p`
  margin: 0 0 12px; font-size: 14px; color: #64748b; line-height: 1.5;
`;
const DemandaFooter = styled.div`display: flex; gap: 16px; align-items: center;`;
const DemandaInfo = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; color: #94a3b8;
  svg { flex-shrink: 0; }
`;

// Shimmer
const ShimmerWrap = styled.div`
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px;
  margin-bottom: 10px;
`;
const ShimmerLine = styled.div<{ width?: string; height?: string }>`
  width: ${p => p.width || '100%'}; height: ${p => p.height || '14px'};
  border-radius: 4px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
  background-size: 200% 100%; animation: ${shimmerAnim} 1.5s infinite;
`;
const ShimmerAvatar = styled.div`
  width: 32px; height: 32px; min-width: 32px; border-radius: 50%;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
  background-size: 200% 100%; animation: ${shimmerAnim} 1.5s infinite;
`;

// Full-page loading placeholder
const LoadingPane = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  color: #9ca3af; font-size: 14px;
`;

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  contatoId: number | null;
  onClose: () => void;
}

const ContactDetailPanel: React.FC<Props> = ({ contatoId, onClose }) => {
  const { session, userData } = useAuth();
  const { can, loading: permissionsLoading } = usePermissionCheck();

  const [contact, setContact] = useState<Eleitor | null>(null);
  const [loadingContact, setLoadingContact] = useState(false);

  const [registros, setRegistros] = useState<Registro[]>([]);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [loadingRegistros, setLoadingRegistros] = useState(false);
  const [loadingLog, setLoadingLog] = useState(false);

  const [demandas, setDemandas] = useState<any[]>([]);
  const [loadingDemandas, setLoadingDemandas] = useState(false);

  const [activeTab, setActiveTab] = useState<'registros' | 'demandas'>('registros');
  const [registrosFilter, setRegistrosFilter] = useState<'tudo' | 'comentarios' | 'historico'>('tudo');

  const [newRegistro, setNewRegistro] = useState('');
  const [addingRegistro, setAddingRegistro] = useState(false);

  const [editingRegistro, setEditingRegistro] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [savingRegistroId, setSavingRegistroId] = useState<number | null>(null);
  const [deletingRegistroId, setDeletingRegistroId] = useState<number | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const [savingField, setSavingField] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [allGroupTags, setAllGroupTags] = useState<Tag[]>([]);

  const contactRef = useRef<Eleitor | null>(null);
  contactRef.current = contact;

  const cities = (citiesData as CityData[]).filter(c => c.microrregiao?.mesorregiao?.UF?.sigla === 'RS');
  const sortedCities = [...cities].sort((a, b) => a.nome.localeCompare(b.nome));

  const canEditContact = !permissionsLoading && (
    can('contacts.edit.all') ||
    (can('contacts.edit.own') && !!contact && (contact as any).assessor?.id === userData?.id)
  );

  // ── Fetch contact ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!contatoId || !session?.access_token) return;

    setContact(null);
    setRegistros([]);
    setLogEntries([]);
    setDemandas([]);
    setActiveTab('registros');
    setNewRegistro('');

    const fetchContact = async () => {
      setLoadingContact(true);
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${contatoId}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const c: Eleitor = data.data ?? data;
          setContact(c);
          setSelectedTags(c.tags || []);
          fetchRegistros(c.id);
          fetchDemandas(c.id);
        }
      } catch (e) {
        console.error('ContactDetailPanel: error fetching contact', e);
      } finally {
        setLoadingContact(false);
      }
    };

    const fetchGroupTags = async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/v1/tags`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) {
          const d = await res.json();
          setAllGroupTags(d.data || []);
        }
      } catch {}
    };

    fetchContact();
    fetchGroupTags();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contatoId, session?.access_token]);

  // ── Data fetchers ──────────────────────────────────────────────────────────
  const fetchRegistros = async (eleitorId: number) => {
    if (!session?.access_token) return;
    setLoadingRegistros(true);
    setLoadingLog(true);
    try {
      const [regRes, logRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/api/v1/eleitores/${eleitorId}/registros`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
        apiFetch(`${API_BASE_URL}/api/v1/eleitores/${eleitorId}/historico`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
      ]);
      if (regRes.ok) { const d = await regRes.json(); setRegistros(d.data || []); }
      if (logRes.ok) { const d = await logRes.json(); setLogEntries(d.data || []); }
    } catch (e) {
      console.error('fetchRegistros error', e);
    } finally {
      setLoadingRegistros(false);
      setLoadingLog(false);
    }
  };

  const fetchDemandas = async (eleitorId: number) => {
    if (!session?.access_token) return;
    setLoadingDemandas(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/demandas?solicitante_id=${eleitorId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) { const d = await res.json(); setDemandas(d.data || []); }
    } catch (e) {
      console.error('fetchDemandas error', e);
    } finally {
      setLoadingDemandas(false);
    }
  };

  // ── Field save ─────────────────────────────────────────────────────────────
  const saveField = async (fieldKey: string, body: Record<string, any>, localPatch?: Partial<Eleitor>) => {
    const c = contactRef.current;
    if (!c || !session?.access_token) return;
    setSavingField(fieldKey);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${c.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Erro ao atualizar');
      const data = await res.json();
      const serverContact = data.data as Eleitor;
      const updated = { ...serverContact, tags: contactRef.current?.tags } as Eleitor;
      setContact(updated);
      fetchRegistros(c.id);
    } catch (e) {
      console.error('saveField error', e);
      alert('Erro ao salvar alteração');
    } finally {
      setSavingField(null);
    }
  };

  const saveContactTags = async (tags: Tag[]) => {
    const c = contactRef.current;
    if (!c || !session?.access_token) return;
    setSelectedTags(tags);
    setSavingField('tags');
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${c.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ tag_ids: tags.map(t => t.id) }),
      });
      if (!res.ok) throw new Error('Erro ao atualizar tags');
      const updated = { ...c, tags } as Eleitor;
      setContact(updated);
      fetchRegistros(c.id);
    } catch (e) {
      console.error('saveContactTags error', e);
      setSelectedTags(c.tags || []);
      alert('Erro ao salvar tags');
    } finally {
      setSavingField(null);
    }
  };

  // ── Registro CRUD ──────────────────────────────────────────────────────────
  const handleAddRegistro = async () => {
    const c = contactRef.current;
    if (!c?.id || !newRegistro.trim() || !session?.access_token) return;
    setAddingRegistro(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${c.id}/registros`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ descricao: newRegistro.trim() }),
      });
      if (res.ok) { setNewRegistro(''); fetchRegistros(c.id); }
      else alert('Erro ao adicionar registro');
    } catch (e) {
      console.error('handleAddRegistro error', e);
      alert('Erro ao adicionar registro');
    } finally {
      setAddingRegistro(false);
    }
  };

  const handleEditRegistro = async (registroId: number) => {
    const c = contactRef.current;
    if (!c?.id || !editingText.trim() || !session?.access_token) return;
    setSavingRegistroId(registroId);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${c.id}/registros/${registroId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ descricao: editingText.trim() }),
      });
      if (res.ok) { setEditingRegistro(null); setEditingText(''); fetchRegistros(c.id); }
      else alert('Erro ao editar registro');
    } catch (e) {
      console.error('handleEditRegistro error', e);
    } finally {
      setSavingRegistroId(null);
    }
  };

  const handleDeleteRegistro = async (registroId: number) => {
    const c = contactRef.current;
    if (!c?.id || !session?.access_token) return;
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return;
    setDeletingRegistroId(registroId);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${c.id}/registros/${registroId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) fetchRegistros(c.id);
      else alert('Erro ao excluir registro');
    } catch (e) {
      console.error('handleDeleteRegistro error', e);
    } finally {
      setDeletingRegistroId(null);
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleWhatsAppClick = (phone: string | null) => {
    if (!phone) return;
    window.open(`https://wa.me/${phone.replace(/\D/g, '')}`, '_blank');
  };
  const handleCallClick = (phone: string | null) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  // ── Timeline helpers ───────────────────────────────────────────────────────
  type CommentItem = Registro & { _source: 'comment' };
  type HistoryItem = LogEntry & { _source: 'history'; created_at: string };
  type TimelineEntry = CommentItem | HistoryItem;

  const buildTimeline = (): { label: string; items: TimelineEntry[] }[] => {
    const all: TimelineEntry[] = [
      ...registros.map(r => ({ ...r, _source: 'comment' as const })),
      ...logEntries.map(l => ({ ...l, _source: 'history' as const, created_at: l.data_hora })),
    ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    const filtered = registrosFilter === 'comentarios' ? all.filter(i => i._source === 'comment')
      : registrosFilter === 'historico' ? all.filter(i => i._source === 'history')
      : all;

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const groups: { label: string; items: TimelineEntry[] }[] = [];
    const seen = new Map<string, number>();

    filtered.forEach(r => {
      const d = new Date(r.created_at); d.setHours(0, 0, 0, 0);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const key = `${d.getFullYear()}-${mm}-${dd}`;
      let label = `${dd}/${mm}`;
      if (d.getTime() === today.getTime()) label = `HOJE · ${dd}/${mm}`;
      else if (d.getTime() === yesterday.getTime()) label = `ONTEM · ${dd}/${mm}`;
      if (!seen.has(key)) { seen.set(key, groups.length); groups.push({ label, items: [] }); }
      groups[seen.get(key)!].items.push(r);
    });

    return groups;
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <PanelWrap>
      {loadingContact || !contact ? (
        <>
          <CDHeader>
            <CDHeaderTop>
              <BackBtn onClick={onClose}>
                <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Voltar
              </BackBtn>
              <ShimmerLine width="180px" height="13px" />
            </CDHeaderTop>
            <ShimmerLine width="260px" height="24px" style={{ marginTop: 8 }} />
          </CDHeader>
          <LoadingPane>{loadingContact ? 'Carregando contato...' : 'Contato não encontrado'}</LoadingPane>
        </>
      ) : (
        <>
          {/* ── HEADER ── */}
          <CDHeader>
            <CDHeaderTop>
              <BackBtn onClick={onClose}>
                <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Voltar
              </BackBtn>
              <CDMeta>
                <CDMetaChip>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12">
                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  CONTATO
                </CDMetaChip>
                <CDMetaDot>•</CDMetaDot>
                <CDMetaText>#{String(contact.id).padStart(4, '0')}</CDMetaText>
                {contact.cidade && (
                  <>
                    <CDMetaDot>•</CDMetaDot>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12" style={{ color: '#6b7280', flexShrink: 0 }}>
                      <path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <CDMetaText>{contact.cidade.nome}</CDMetaText>
                  </>
                )}
                {contact.profissao && (
                  <>
                    <CDMetaDot>•</CDMetaDot>
                    <CDMetaText>{contact.profissao}</CDMetaText>
                  </>
                )}
              </CDMeta>
            </CDHeaderTop>

            <CDHeaderBottom>
              <CDNameRow>
                <CDName>
                  <InlineEditText
                    value={contact.nome || ''}
                    canEdit={canEditContact}
                    saving={savingField === 'nome'}
                    onSave={v => saveField('nome', { nome: v })}
                    placeholder="Nome completo"
                    allowEmpty={false}
                  />
                </CDName>
                {contact.multiplicador && (
                  <CDMultiplicadorBadge>
                    <svg fill="currentColor" viewBox="0 0 20 20" width="12" height="12">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    MULTIPLICADOR
                  </CDMultiplicadorBadge>
                )}
              </CDNameRow>

              <CDActionBtns>
                {contact.telefone && (
                  <>
                    <CDActionBtn $primary onClick={() => handleWhatsAppClick(contact.telefone)}>
                      <svg fill="currentColor" viewBox="0 0 24 24" width="15" height="15">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                      WhatsApp
                    </CDActionBtn>
                    <CDActionBtn onClick={() => handleCallClick(contact.telefone)}>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="15" height="15">
                        <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      Ligar
                    </CDActionBtn>
                  </>
                )}
              </CDActionBtns>
            </CDHeaderBottom>
          </CDHeader>

          {/* ── BODY ── */}
          <CDBody>
            {/* LEFT: info panel */}
            <CDLeft>
              <CDInfoColumns>
                {/* CONTATO */}
                <CDSection>
                  <CDSectionTitle>CONTATO</CDSectionTitle>
                  <CDInfoGrid>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Telefone
                      </CDInfoLabel>
                      <CDInfoValue style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <InlineEditText
                          value={contact.telefone || ''}
                          canEdit={canEditContact}
                          saving={savingField === 'telefone'}
                          onSave={v => saveField('telefone', { telefone: v || null })}
                          placeholder="(00) 00000-0000"
                          emptyLabel="-"
                          renderDisplay={v => <span>{v === '-' ? '-' : formatPhone(v)}</span>}
                        />
                        {contact.telefone && (
                          <>
                            <InlinePhoneButton type="button" title="WhatsApp" $color="#25d366" onClick={() => handleWhatsAppClick(contact.telefone)}>
                              <svg fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                            </InlinePhoneButton>
                            <InlinePhoneButton type="button" title="Ligar" $color="var(--accent)" onClick={() => handleCallClick(contact.telefone)}>
                              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                            </InlinePhoneButton>
                          </>
                        )}
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Email
                      </CDInfoLabel>
                      <CDInfoValue>
                        <InlineEditText
                          value={contact.email || ''}
                          canEdit={canEditContact}
                          saving={savingField === 'email'}
                          onSave={v => saveField('email', { email: v || null })}
                          placeholder="email@exemplo.com"
                          emptyLabel="-"
                        />
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Cidade
                      </CDInfoLabel>
                      <CDInfoValue>
                        <InlineEditSelect
                          value={contact.cidade_id != null ? String(contact.cidade_id) : undefined}
                          canEdit={canEditContact}
                          saving={savingField === 'cidade_id'}
                          options={sortedCities.map(c => ({ value: String(c.id), label: `${c.nome} - ${c.microrregiao.mesorregiao.UF.sigla}` }))}
                          searchable
                          searchPlaceholder="Buscar cidade..."
                          emptyLabel="-"
                          renderDisplay={() => <span>{contact.cidade ? `${contact.cidade.nome} - ${contact.cidade.uf}` : '-'}</span>}
                          onSave={v => {
                            const id = parseInt(v, 10);
                            const city = sortedCities.find(c => c.id === id);
                            return saveField('cidade_id', { cidade_id: id }, {
                              cidade_id: id,
                              cidade: city ? { id, nome: city.nome, uf: city.microrregiao.mesorregiao.UF.sigla } : undefined,
                            });
                          }}
                        />
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Nascimento
                      </CDInfoLabel>
                      <CDInfoValue>
                        <InlineEditDate
                          value={contact.data_nascimento ?? undefined}
                          canEdit={canEditContact}
                          saving={savingField === 'data_nascimento'}
                          onSave={v => saveField('data_nascimento', { data_nascimento: v })}
                          emptyLabel="-"
                          renderDisplay={formatted => {
                            if (!contact.data_nascimento || formatted === '-') return <span>-</span>;
                            const parts = contact.data_nascimento.slice(0, 10).split('-');
                            const birthDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                            const today = new Date();
                            const age = today.getFullYear() - birthDate.getFullYear()
                              - (today < new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate()) ? 1 : 0);
                            const isToday = birthDate.getDate() === today.getDate() && birthDate.getMonth() === today.getMonth();
                            return (
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {birthDate.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })} · {age} anos
                                {isToday && <CDTodayBadge>Hoje</CDTodayBadge>}
                              </span>
                            );
                          }}
                        />
                      </CDInfoValue>
                    </CDInfoRow>

                  </CDInfoGrid>
                </CDSection>

                {/* SOBRE */}
                <CDSection>
                  <CDSectionTitle>SOBRE</CDSectionTitle>
                  <CDInfoGrid>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Profissão
                      </CDInfoLabel>
                      <CDInfoValue>
                        <InlineEditText
                          value={contact.profissao || ''}
                          canEdit={canEditContact}
                          saving={savingField === 'profissao'}
                          onSave={v => saveField('profissao', { profissao: v || null })}
                          placeholder="Profissão"
                          emptyLabel="-"
                        />
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Time do coração
                      </CDInfoLabel>
                      <CDInfoValue>
                        <InlineEditSelect
                          value={contact.time_coracao ?? undefined}
                          canEdit={canEditContact}
                          saving={savingField === 'time_coracao'}
                          options={timesRS.map(t => ({ value: t, label: t }))}
                          emptyLabel="-"
                          onSave={v => saveField('time_coracao', { time_coracao: v })}
                        />
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        Multiplicador
                      </CDInfoLabel>
                      <CDInfoValue>
                        <HoverShell $canEdit={canEditContact}
                          onClick={() => canEditContact && saveField('multiplicador', { multiplicador: !contact.multiplicador })}>
                          {savingField === 'multiplicador' ? <SpinnerCircle /> : (
                            <MultiplicadorBadge $active={contact.multiplicador ?? false}>
                              {contact.multiplicador ? 'Sim' : 'Não'}
                            </MultiplicadorBadge>
                          )}
                        </HoverShell>
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow style={{ alignItems: 'flex-start', minHeight: 'auto', paddingTop: 4 }}>
                      <CDInfoLabel style={{ paddingTop: 4 }}>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        Tags
                      </CDInfoLabel>
                      <CDInfoValue style={{ flexWrap: 'wrap', alignItems: 'flex-start', paddingTop: 2 }}>
                        <TagSelector
                          selectedTags={selectedTags}
                          onChange={saveContactTags}
                          onTagCreated={tag => setAllGroupTags(prev => [...prev, tag].sort((a, b) => a.nome.localeCompare(b.nome)))}
                          disabled={!canEditContact}
                        />
                      </CDInfoValue>
                    </CDInfoRow>

                  </CDInfoGrid>
                </CDSection>
              </CDInfoColumns>

              {/* REDES SOCIAIS */}
              <CDSection style={{ maxWidth: 'calc(50% - 12px)' }}>
                <CDSectionTitle>REDES SOCIAIS</CDSectionTitle>
                <CDInfoGrid>
                  <CDInfoRow>
                    <CDInfoLabel>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Rede Social
                    </CDInfoLabel>
                    <CDInfoValue>
                      <InlineEditText
                        value={contact.rede_social || ''}
                        canEdit={canEditContact}
                        saving={savingField === 'rede_social'}
                        onSave={v => saveField('rede_social', { rede_social: v || null })}
                        placeholder="@usuario ou link"
                        emptyLabel="-"
                      />
                    </CDInfoValue>
                  </CDInfoRow>
                </CDInfoGrid>
              </CDSection>

              {/* NOTAS INTERNAS */}
              <CDSection>
                <CDSectionTitle>NOTAS INTERNAS</CDSectionTitle>
                <InlineEditTextArea
                  value={contact.observacoes || ''}
                  canEdit={canEditContact}
                  saving={savingField === 'observacoes'}
                  onSave={v => saveField('observacoes', { observacoes: v || null })}
                  placeholder="Anotações e observações sobre o contato..."
                  emptyLabel="Sem notas internas"
                />
              </CDSection>
            </CDLeft>

            {/* RIGHT: registros / demandas */}
            <CDRight>
              <CDRightTabs>
                <CDRightTab $active={activeTab === 'registros'} onClick={() => setActiveTab('registros')}>
                  <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" width="14" height="14">
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Registros {(registros.length + logEntries.length) > 0 && <CDRightTabCount>{registros.length + logEntries.length}</CDRightTabCount>}
                </CDRightTab>
                <CDRightTab $active={activeTab === 'demandas'} onClick={() => setActiveTab('demandas')}>
                  <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" width="14" height="14">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Demandas {demandas.length > 0 && <CDRightTabCount>{demandas.length}</CDRightTabCount>}
                </CDRightTab>
              </CDRightTabs>

              {/* ── REGISTROS TAB ── */}
              {activeTab === 'registros' && (() => {
                const groups = buildTimeline();
                const isLoading = loadingRegistros || loadingLog;

                return (
                  <>
                    <CDFilterBar>
                      <CDFilterChips>
                        <CDFilterChip $active={registrosFilter === 'tudo'} onClick={() => setRegistrosFilter('tudo')}>Tudo</CDFilterChip>
                        <CDFilterChip $active={registrosFilter === 'comentarios'} onClick={() => setRegistrosFilter('comentarios')}>Comentários</CDFilterChip>
                        <CDFilterChip $active={registrosFilter === 'historico'} onClick={() => setRegistrosFilter('historico')}>Histórico</CDFilterChip>
                      </CDFilterChips>
                    </CDFilterBar>

                    <CDRightBody>
                      {isLoading ? (
                        <>
                          {[1, 2, 3].map(i => (
                            <ShimmerWrap key={i}>
                              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                                <ShimmerAvatar />
                                <div style={{ flex: 1 }}>
                                  <ShimmerLine width="120px" height="13px" />
                                  <ShimmerLine width="80px" height="11px" style={{ marginTop: 5 }} />
                                </div>
                              </div>
                              <ShimmerLine width="100%" height="13px" />
                              <ShimmerLine width="80%" height="13px" style={{ marginTop: 6 }} />
                            </ShimmerWrap>
                          ))}
                        </>
                      ) : groups.length === 0 ? (
                        <CDEmptyState>
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                          </svg>
                          <span>Nenhum registro ainda</span>
                        </CDEmptyState>
                      ) : (
                        <CDTimeline>
                          {groups.map(({ label, items }, gi) => (
                            <React.Fragment key={label}>
                              <CDDateGroup>{label}</CDDateGroup>
                              {items.map((entry, idx) => {
                                const isLast = gi === groups.length - 1 && idx === items.length - 1;
                                const time = new Date(entry.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

                                if (entry._source === 'history') {
                                  const hist = entry as HistoryItem;
                                  return (
                                    <CDTimelineItem key={`log-${hist.id}`}>
                                      <CDTimelineLeft>
                                        <CDIconAvatar $type="history">
                                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="13" height="13">
                                            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        </CDIconAvatar>
                                        {!isLast && <CDTimelineLine />}
                                      </CDTimelineLeft>
                                      <CDTimelineContent>
                                        <CDFeedRow>
                                          <CDFeedMeta>
                                            <CDFeedAuthor>{hist.registrou?.nome || 'Sistema'}</CDFeedAuthor>
                                            <CDFeedAction> {hist.observacao}</CDFeedAction>
                                          </CDFeedMeta>
                                          <CDFeedTime>{time}</CDFeedTime>
                                        </CDFeedRow>
                                      </CDTimelineContent>
                                    </CDTimelineItem>
                                  );
                                }

                                const reg = entry as CommentItem;
                                return (
                                  <CDTimelineItem key={`reg-${reg.id}`}>
                                    <CDTimelineLeft>
                                      <CDIconAvatar $type="comment">
                                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="13" height="13">
                                          <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      </CDIconAvatar>
                                      {!isLast && <CDTimelineLine />}
                                    </CDTimelineLeft>
                                    <CDTimelineContent>
                                      <CDFeedRow>
                                        <CDFeedMeta>
                                          <CDFeedAuthor>{reg.usuario.nome}</CDFeedAuthor>
                                          <CDFeedAction> comentou</CDFeedAction>
                                        </CDFeedMeta>
                                        <CDFeedTime>{time}</CDFeedTime>
                                        {userData?.id && reg.usuario.id === userData.id && (
                                          <CDRegistroMenuWrap>
                                            <CDRegistroMenuBtn onClick={() => setOpenMenuId(openMenuId === reg.id ? null : reg.id)}>
                                              <svg fill="currentColor" viewBox="0 0 24 24" width="14" height="14">
                                                <path d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                              </svg>
                                            </CDRegistroMenuBtn>
                                            {openMenuId === reg.id && (
                                              <RegistroMenu>
                                                <RegistroMenuItem onClick={() => { setEditingRegistro(reg.id); setEditingText(reg.descricao); setOpenMenuId(null); }}>
                                                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                  Editar
                                                </RegistroMenuItem>
                                                <RegistroMenuItem $danger
                                                  onClick={() => { setOpenMenuId(null); handleDeleteRegistro(reg.id); }}
                                                  disabled={deletingRegistroId === reg.id}>
                                                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                  Excluir
                                                </RegistroMenuItem>
                                              </RegistroMenu>
                                            )}
                                          </CDRegistroMenuWrap>
                                        )}
                                      </CDFeedRow>
                                      {editingRegistro === reg.id ? (
                                        <EditRegistroForm style={{ marginTop: 6 }}>
                                          <EditTextArea value={editingText} onChange={e => setEditingText(e.target.value)} rows={3} />
                                          <EditActions>
                                            <SaveButton onClick={() => handleEditRegistro(reg.id)} disabled={savingRegistroId === reg.id}>
                                              {savingRegistroId === reg.id ? <SpinnerBtn /> : 'Salvar'}
                                            </SaveButton>
                                            <CancelEditButton onClick={() => { setEditingRegistro(null); setEditingText(''); }}>Cancelar</CancelEditButton>
                                          </EditActions>
                                        </EditRegistroForm>
                                      ) : (
                                        <CDFeedCard>{reg.descricao}</CDFeedCard>
                                      )}
                                    </CDTimelineContent>
                                  </CDTimelineItem>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </CDTimeline>
                      )}
                    </CDRightBody>

                    <CDRightFooter>
                      <CDInputBox>
                        <CDInputArea
                          value={newRegistro}
                          onChange={e => setNewRegistro(e.target.value)}
                          placeholder="Anote uma conversa, lembrete ou observação..."
                          rows={3}
                          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddRegistro(); }}
                        />
                        <CDFooterRow>
                          <CDSendBtn onClick={handleAddRegistro} disabled={!newRegistro.trim() || addingRegistro}>
                            {addingRegistro ? 'Enviando...' : 'Registrar'}
                          </CDSendBtn>
                        </CDFooterRow>
                      </CDInputBox>
                    </CDRightFooter>
                  </>
                );
              })()}

              {/* ── DEMANDAS TAB ── */}
              {activeTab === 'demandas' && (
                <CDRightBody>
                  {loadingDemandas ? (
                    <CDEmptyState><span>Carregando...</span></CDEmptyState>
                  ) : demandas.length === 0 ? (
                    <CDEmptyState>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>Nenhuma demanda encontrada</span>
                    </CDEmptyState>
                  ) : (
                    demandas.map((demanda: any) => (
                      <DemandaItem key={demanda.id} style={{ marginBottom: 10 }}>
                        <DemandaHeader>
                          <DemandaTitle>{demanda.titulo}</DemandaTitle>
                          <DemandaStatus $status={demanda.status}>
                            {demanda.status === 'NAO_INICIADA' && 'Não Iniciada'}
                            {demanda.status === 'EM_ANDAMENTO' && 'Em Andamento'}
                            {demanda.status === 'PAUSADA' && 'Pausada'}
                            {demanda.status === 'CONCLUIDA' && 'Concluída'}
                            {demanda.status === 'CANCELADA' && 'Cancelada'}
                          </DemandaStatus>
                        </DemandaHeader>
                        {demanda.descricao && <DemandaDescription>{demanda.descricao}</DemandaDescription>}
                        <DemandaFooter>
                          <DemandaInfo>
                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
                              <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {new Date(demanda.created_at).toLocaleDateString('pt-BR')}
                          </DemandaInfo>
                          {demanda.tipo && (
                            <DemandaInfo>
                              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
                                <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              {demanda.tipo}
                            </DemandaInfo>
                          )}
                        </DemandaFooter>
                      </DemandaItem>
                    ))
                  )}
                </CDRightBody>
              )}
            </CDRight>
          </CDBody>
        </>
      )}
    </PanelWrap>
  );
};

export default ContactDetailPanel;
